import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Cookie } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { getConsent, saveConsent, OPEN_SETTINGS_EVENT } from '@/lib/consent';

const CATEGORIES = [
  { key: 'necessary', title: 'Necessários', text: 'Sessão, segurança, verificação de idade e a tua escolha de cookies. Sempre ativos.', locked: true },
  { key: 'statistics', title: 'Estatísticas', text: 'Cookie próprio que distingue visitas novas e recorrentes, e Google Analytics se for ativado. Sem estes, só contamos visitas de forma agregada e anónima.' },
  { key: 'session_recording', title: 'Gravação de sessão', text: 'Microsoft Clarity (mapas de cliques e gravação anónima da navegação), se for ativado.' },
];

const Preferences = ({ open, onOpenChange }) => {
  const current = getConsent();
  const [prefs, setPrefs] = useState({ statistics: !!current?.statistics, session_recording: !!current?.session_recording });
  useEffect(() => {
    if (open) {
      const c = getConsent();
      setPrefs({ statistics: !!c?.statistics, session_recording: !!c?.session_recording });
    }
  }, [open]);
  const save = (p) => { saveConsent(p); onOpenChange(false); };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg bg-card border-border" data-testid="cookie-settings-dialog">
        <DialogHeader>
          <DialogTitle>Definições de cookies</DialogTitle>
          <DialogDescription>Escolhe o que aceitas. Podes mudar a qualquer momento no rodapé do site.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          {CATEGORIES.map((c) => (
            <div key={c.key} className="flex items-start justify-between gap-4 rounded-lg border border-border p-3">
              <div>
                <p className="text-sm font-medium text-foreground">{c.title}</p>
                <p className="text-xs text-muted-foreground mt-1">{c.text}</p>
              </div>
              <Switch
                checked={c.locked ? true : prefs[c.key]}
                disabled={c.locked}
                onCheckedChange={(v) => setPrefs((p) => ({ ...p, [c.key]: v }))}
                data-testid={`cookie-toggle-${c.key}`}
              />
            </div>
          ))}
        </div>
        <div className="flex flex-col-reverse sm:flex-row gap-2 sm:justify-end pt-2">
          <Button variant="outline" onClick={() => save({ statistics: false, session_recording: false })} data-testid="cookie-settings-reject">Recusar todos</Button>
          <Button variant="outline" onClick={() => save(prefs)} data-testid="cookie-settings-save">Guardar escolha</Button>
          <Button className="bg-ruby hover:bg-ruby-light" onClick={() => save({ statistics: true, session_recording: true })} data-testid="cookie-settings-accept">Aceitar todos</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default function CookieConsent() {
  const [decided, setDecided] = useState(!!getConsent());
  const [settingsOpen, setSettingsOpen] = useState(false);

  useEffect(() => {
    const open = () => setSettingsOpen(true);
    window.addEventListener(OPEN_SETTINGS_EVENT, open);
    return () => window.removeEventListener(OPEN_SETTINGS_EVENT, open);
  }, []);
  useEffect(() => { if (!settingsOpen) setDecided(!!getConsent()); }, [settingsOpen]);

  const choose = (all) => { saveConsent({ statistics: all, session_recording: all }); setDecided(true); };

  return (
    <>
      {!decided && !settingsOpen && (
        <div className="fixed inset-x-3 bottom-3 z-[60] sm:inset-x-auto sm:right-5 sm:bottom-5 sm:max-w-md" data-testid="cookie-banner">
          <div className="rounded-xl border border-border bg-card/95 backdrop-blur-md p-5 shadow-2xl">
            <div className="flex items-start gap-3">
              <Cookie className="w-5 h-5 text-gold shrink-0 mt-0.5" />
              <p className="text-sm text-muted-foreground">
                Usamos cookies necessários para o site funcionar. Com a tua autorização, usamos também cookies de estatística e de gravação de sessão.{' '}
                <Link to="/cookies" className="text-ruby hover:underline">Saber mais</Link>
              </p>
            </div>
            <div className="mt-4 grid grid-cols-3 gap-2">
              <Button variant="outline" size="sm" onClick={() => choose(false)} data-testid="cookie-reject">Recusar</Button>
              <Button variant="outline" size="sm" onClick={() => setSettingsOpen(true)} data-testid="cookie-customize">Personalizar</Button>
              <Button variant="outline" size="sm" onClick={() => choose(true)} data-testid="cookie-accept">Aceitar</Button>
            </div>
          </div>
        </div>
      )}
      <Preferences open={settingsOpen} onOpenChange={setSettingsOpen} />
    </>
  );
}
